<script setup lang="ts">
  import { computed, onBeforeMount, ref } from 'vue';

  import type { SMSThread } from '@stuut/api-sdk/models';
  import type { Call, Email, EmailThread, PaymentPromise } from '@stuut/api/models';
  import { PaymentPromiseSource } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ui/components/hover-card';
  import { Timestamp } from '@ui/components/time';

  import { stuut } from '@/api';
  import { CallDrawer } from '@/components/calls';
  import { EmailThreadDrawer } from '@/components/email-threads';
  import { SMSDrawer } from '@/components/sms';
  import UserAvatar from '@/components/users/UserAvatar.vue';
  import { sdk } from '@/sdk';
  import { useUsers } from '@/store';

  interface Props {
    promise: PaymentPromise;
    variant?: 'icon' | 'label';
  }

  const props = withDefaults(defineProps<Props>(), {
    variant: 'icon',
  });

  const labelText = computed(() => {
    switch (props.promise.source) {
      case PaymentPromiseSource.EMAIL:
        return 'Email';
      case PaymentPromiseSource.CALL:
        return 'Call';
      case PaymentPromiseSource.SMS:
        return 'SMS';
      case PaymentPromiseSource.USER:
        return 'Manual';
      case PaymentPromiseSource.INTEGRATION_SYNC:
        return 'Sync';
      default:
        return '';
    }
  });

  const loading = ref(false);

  const emailThread = ref<EmailThread>();
  const selectedEmailId = ref<string>();
  const emailDrawerOpen = ref(false);

  const call = ref<Call>();
  const callDrawerOpen = ref(false);

  const smsThread = ref<SMSThread>();
  const smsDrawerOpen = ref(false);

  const userStore = useUsers();

  const needsUser = computed(
    () => props.promise.source === PaymentPromiseSource.USER && !!props.promise.created_by_user_id,
  );

  onBeforeMount(() => {
    if (needsUser.value && !userStore.users.length) userStore.fetchUsers();
  });

  const user = computed(() =>
    needsUser.value ? userStore.getUserById(props.promise.created_by_user_id!) : undefined,
  );

  const userLoading = computed(() => needsUser.value && !user.value && userStore.loading);

  const openEmail = async () => {
    if (loading.value || !props.promise.email_id) return;
    loading.value = true;
    try {
      const email: Email = await stuut.emails.retrieve(props.promise.email_id);
      emailThread.value = await stuut.emailThreads.retrieve(email.thread_id);
      selectedEmailId.value = email.id;
      emailDrawerOpen.value = true;
    } finally {
      loading.value = false;
    }
  };

  const openCall = async () => {
    if (loading.value || !props.promise.call_id) return;
    loading.value = true;
    try {
      call.value = await stuut.calls.retrieve(props.promise.call_id);
      callDrawerOpen.value = true;
    } finally {
      loading.value = false;
    }
  };

  const openSms = async () => {
    if (loading.value || !props.promise.sms_thread_id) return;
    loading.value = true;
    try {
      smsThread.value = await sdk.smsThreads.get({
        smsThreadId: props.promise.sms_thread_id,
      });
      smsDrawerOpen.value = true;
    } finally {
      loading.value = false;
    }
  };
</script>

<template>
  <template v-if="promise.source">
    <HoverCard :open-delay="100">
      <HoverCardTrigger as-child>
        <button
          type="button"
          class="inline-flex items-center cursor-pointer"
          :class="
            variant === 'label'
              ? 'text-xs underline decoration-dashed decoration-muted-foreground decoration-1 underline-offset-4 hover:text-primary'
              : 'text-muted-foreground hover:text-foreground'
          "
        >
          <template v-if="variant === 'label'">{{ labelText }}</template>
          <i v-else class="far fa-info-circle text-2xs" />
        </button>
      </HoverCardTrigger>

      <HoverCardContent class="w-auto p-3" align="start">
        <div class="text-xs flex items-center gap-x-2">
          <template v-if="promise.source === PaymentPromiseSource.EMAIL">
            <span>
              Promised via email ·
              <Timestamp :time="promise.created_at" format="L" date-only />
            </span>
            <Button variant="link" size="inline" :disabled="loading" @click="openEmail">
              Open thread
              <i v-if="loading" class="fa fa-spinner-third animate-spin text-2xs!" />
            </Button>
          </template>

          <template v-else-if="promise.source === PaymentPromiseSource.CALL">
            <span>
              Promised via call ·
              <Timestamp :time="promise.created_at" format="L" date-only />
            </span>
            <Button variant="link" size="inline" :disabled="loading" @click="openCall">
              Open call
              <i v-if="loading" class="fa fa-spinner-third animate-spin text-2xs!" />
            </Button>
          </template>

          <template v-else-if="promise.source === PaymentPromiseSource.SMS">
            <span>
              Promised via SMS ·
              <Timestamp :time="promise.created_at" format="L" date-only />
            </span>
            <Button variant="link" size="inline" :disabled="loading" @click="openSms">
              Open thread
              <i v-if="loading" class="fa fa-spinner-third animate-spin text-2xs!" />
            </Button>
          </template>

          <template v-else-if="promise.source === PaymentPromiseSource.USER">
            <template v-if="user">
              <UserAvatar size="sm" :user="user" />
              <span>
                Updated by {{ user.displayName }} ·
                <Timestamp :time="promise.created_at" format="L" date-only />
              </span>
            </template>
            <span v-else-if="userLoading" class="text-muted-foreground">Loading…</span>
            <span v-else>
              Updated manually ·
              <Timestamp :time="promise.created_at" format="L" date-only />
            </span>
          </template>

          <template v-else-if="promise.source === PaymentPromiseSource.INTEGRATION_SYNC">
            <span>
              Updated from remote ·
              <Timestamp :time="promise.created_at" format="L" date-only />
            </span>
          </template>
        </div>
      </HoverCardContent>
    </HoverCard>

    <EmailThreadDrawer
      v-if="emailDrawerOpen && emailThread"
      v-model:open="emailDrawerOpen"
      :thread="emailThread"
      :selected-email-id="selectedEmailId"
      is-slider
      default-open
      class="h-full min-h-full"
      @close="emailDrawerOpen = false"
    />

    <CallDrawer
      v-if="callDrawerOpen && call"
      v-model:open="callDrawerOpen"
      :call="call"
      @close="callDrawerOpen = false"
    />

    <SMSDrawer
      v-if="smsDrawerOpen && smsThread"
      v-model:open="smsDrawerOpen"
      :thread="smsThread"
      @close="smsDrawerOpen = false"
    />
  </template>
</template>
